/**
 * <oilogic-hero-carousel>
 * Loads Embla Carousel on demand (self-hosted UMD global) and wires up the hero:
 * centered peek layout with loop + drag, generated pagination dots, keyboard nav,
 * an accessible live region, and a one-shot staggered line-reveal on the active
 * slide heading. Progressive enhancement over the server-rendered slides.
 *
 * Embla is used instead of a clone-based looper because its transform-based loop
 * handles variable-width, centered slides without the blank-peek gaps that
 * clone-based loopers exhibit with only a few slides.
 */
class OilogicHeroCarousel extends HTMLElement {
  connectedCallback() {
    this._onSectionLoad = null;
    if (this.hasAttribute('data-initialized')) return;
    this.setAttribute('data-initialized', '');
    this._loadEmbla()
      .then(() => this._init())
      .catch((err) => {
        if (window.Shopify?.designMode) console.warn('oilogic-hero-carousel:', err);
      });
  }

  _loadEmbla() {
    if (typeof window.oilogicEmbla !== 'function') {
      return Promise.reject(new Error('oilogic-hero-carousel: embla-loader.js not loaded'));
    }
    // Shared singleton loader — Embla is fetched at most once per page.
    return window.oilogicEmbla(this.dataset.emblaUrl);
  }

  _init() {
    if (!this.isConnected) return;
    this.viewport = this.querySelector('[data-embla-viewport]');
    if (!this.viewport || !window.EmblaCarousel) return;

    this.controller = new AbortController();
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Drop the CSS pre-init layout in the same synchronous block that hands
    // control to Embla — the swap never gets a chance to paint.
    this.classList.add('is-ready');

    const plugins = [];
    if (this.hasAttribute('data-autoplay') && !prefersReducedMotion && typeof EmblaCarouselAutoplay !== 'undefined') {
      const secs = parseFloat(this.getAttribute('data-autoplay-speed')) || 4;
      plugins.push(
        EmblaCarouselAutoplay({
          delay: secs * 1000,
          stopOnInteraction: false,
          stopOnMouseEnter: true,
          stopOnFocusIn: true,
        })
      );
    }

    this.embla = window.EmblaCarousel(
      this.viewport,
      {
        loop: true,
        align: 'center',
        // If content is ever too short to loop (few/narrow slides), keep the
        // active slide centered instead of trimming snaps to the left edge.
        containScroll: false,
        dragFree: false,
        duration: prefersReducedMotion ? 0 : 25,
      },
      plugins
    );

    this.slides = this.embla.slideNodes();
    this._markImagesLoaded();
    this._buildDots();

    // Entrance: is-enhancing (the CSS pre-animation hidden states) and the
    // animation start in the same synchronous block, so there is never a
    // hidden-but-not-animating frame — the pre-JS hero paints complete, then
    // the reveal plays as soon as Embla is ready.
    this.classList.add('is-enhancing');

    this.embla.on('select', () => this._onSelect());
    this.embla.on('reInit', () => { this._buildDots(); this._onSelect(); });

    // Initial state: reveal on the first active slide.
    this._onSelect();

    // Keyboard: arrow keys navigate when focus is anywhere inside the carousel.
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { this.embla.scrollPrev(); }
      else if (event.key === 'ArrowRight') { this.embla.scrollNext(); }
    }, { signal: this.controller.signal });

    // Clicking a peek (non-active) slide's image centers it instead of
    // navigating; clicking the already-active slide's image navigates.
    this.addEventListener('click', (event) => {
      const link = event.target.closest('.hero-home__media-link');
      if (!link) return;

      const slideEl = link.closest('.hero-home__slide');
      const index = this.slides.indexOf(slideEl);
      if (index === -1) return;

      if (index !== this.embla.selectedScrollSnap()) {
        event.preventDefault();
        this.embla.scrollTo(index);
      }
    }, { signal: this.controller.signal });

    this._onSectionLoad = (e) => {
      if (!e.target.contains(this)) return;
      this._teardown();
      this.removeAttribute('data-initialized');
      this.connectedCallback();
    };
    document.addEventListener('shopify:section:load', this._onSectionLoad);
  }

  _onSelect() {
    const selected = this.embla.selectedScrollSnap();

    this.slides.forEach((slide, i) => {
      const active = i === selected;
      slide.classList.toggle('is-selected', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      // One-shot: never removed, so revisiting a slide keeps its heading in place.
      if (active) slide.classList.add('is-revealed');
    });

    if (this.dots) {
      this.dots.forEach((dot, i) => {
        const active = i === selected;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    this._announceSlide(selected + 1, this.slides.length);
  }

  _buildDots() {
    const nav = this.querySelector('[data-embla-dots]');
    if (!nav) return;
    nav.innerHTML = '';
    const bulletLabel = this.dataset.a11yBullet || 'Go to slide {{index}}';

    this.dots = this.embla.scrollSnapList().map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'hero-home__dot';
      dot.setAttribute('aria-label', bulletLabel.replace('{{index}}', i + 1).replace('{{ index }}', i + 1));
      dot.addEventListener('click', () => this.embla.scrollTo(i), { signal: this.controller.signal });
      nav.appendChild(dot);
      return dot;
    });
  }

  _markImagesLoaded() {
    this.querySelectorAll('.hero-home__slide-image').forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('is-loaded');
      } else {
        img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
        img.addEventListener('error', () => img.classList.add('is-loaded'), { once: true });
      }
    });
  }

  _announceSlide(index, total) {
    const statusEl = this.querySelector('.hero-home__slide-status');
    if (!statusEl) return;
    const template = this.dataset.a11ySlideStatus || 'Slide [[index]] of [[count]]';
    statusEl.textContent = template
      .replace('[[index]]', index)
      .replace('[[count]]', total);
  }

  _teardown() {
    if (this.embla) { this.embla.destroy(); this.embla = null; }
    if (this.controller) this.controller.abort();
    this.classList.remove('is-ready'); // restore the CSS pre-init layout
  }

  disconnectedCallback() {
    this._teardown();
    if (this._onSectionLoad) {
      document.removeEventListener('shopify:section:load', this._onSectionLoad);
    }
  }
}

if (!customElements.get('oilogic-hero-carousel')) {
  customElements.define('oilogic-hero-carousel', OilogicHeroCarousel);
}
